import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatters } from '@/hooks'
import { isTranscriptionSettled } from '@/lib/transcriptions'

import styles from './transcriptionState.module.css'

/**
 * A transcription state with the console's colour plus text convention.
 *
 * The states are their own vocabulary - resolving, submitting, recovery window
 * exceeded - so the generic task badge would flatten several of them into
 * "unknown". Both the full admin page and the simple extraction page use this
 * one component so the two cannot drift apart.
 */
export function TranscriptionStateBadge({ state }: { state: string }) {
  const { t } = useTranslation('console')
  return (
    <span className={styles.state} data-state={state}>
      {t(`transcriptions.states.${state}`, { defaultValue: state })}
    </span>
  )
}

export interface TranscriptionElapsedProps {
  createdAt: string
  updatedAt: string
  state: string
}

/** Live while a job is working; frozen at the last update once it settles. */
export function TranscriptionElapsed({ createdAt, updatedAt, state }: TranscriptionElapsedProps) {
  const { t } = useTranslation('console')
  const fmt = useFormatters()
  const [now, setNow] = useState(() => Date.now())
  const settled = isTranscriptionSettled(state)

  useEffect(() => {
    if (settled) return
    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, 1_000)
    return () => {
      window.clearInterval(timer)
    }
  }, [settled])

  const end = settled ? Date.parse(updatedAt) : now
  const milliseconds = Math.max(0, end - Date.parse(createdAt))
  return <span>{t('transcriptions.elapsed', { time: fmt.duration(milliseconds) })}</span>
}
